import { useCallback, useMemo } from 'react';
import { keepPreviousData } from '@tanstack/react-query';

import { useReportFilters } from '@/admin/common/reportFilters/useReportFilters';
import { useGetMetrics } from '@/common/api/endpoints/reports/reports';
import type { MetricsResponse } from '@/common/api/models/metricsResponse';
import type { MetricsFilters } from '@/common/api/models/metricsFilters';

import type { DashboardDefinition } from './defineDashboard';
import { asNumberFigure, findFigure } from './dashboardFigures';

export type DashboardStatus = 'loading' | 'ready' | 'empty' | 'error';

export function useDashboard(definition: DashboardDefinition) {
  const {
    appliedFilters, setFilter, resetFilters, urlParamKeys,
  } = useReportFilters();

  const queryParams = useMemo(
    () => ({
      metrics: definition.metrics.join(','),
      gated: appliedFilters.gated,
      from: appliedFilters.from,
      to: appliedFilters.to,
      excludePasteHeavy: appliedFilters.excludePasteHeavy,
      excludeFirstTryPasses: appliedFilters.excludeFirstTryPasses,
      ...(appliedFilters.moduleIds.length > 0 ? { moduleIds: appliedFilters.moduleIds } : {}),
    }),
    [appliedFilters, definition.metrics],
  );

  const {
    data: metrics,
    error,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetMetrics(queryParams, {
    query: {
      select: (res) => res?.data?.data ?? null,
      placeholderData: keepPreviousData,
    },
    // FastAPI binds a repeated `moduleIds` key. Axios otherwise sends `moduleIds[]`.
    axios: { paramsSerializer: { indexes: null } },
  });

  const figure = useCallback(
    (name: string) => findFigure(metrics?.figures, name),
    [metrics?.figures],
  );

  const scopeFigure = useMemo(
    () => asNumberFigure(findFigure(metrics?.figures, 'ai_assist_scope')),
    [metrics?.figures],
  );

  const attemptsFigure = useMemo(
    () => asNumberFigure(findFigure(metrics?.figures, 'attempts_to_done')),
    [metrics?.figures],
  );

  const status: DashboardStatus = useMemo(() => {
    if (isLoading && !metrics) return 'loading';
    if (isError) return 'error';
    if (!metrics) return 'error';
    const scopeValue = scopeFigure?.value;
    const scopeN = scopeFigure?.extras?.n ?? 0;
    if (scopeValue == null && scopeN === 0) return 'empty';
    if (!attemptsFigure || attemptsFigure.shape !== 'number') return 'error';
    if (attemptsFigure.value != null && typeof attemptsFigure.value !== 'number') return 'error';
    return 'ready';
  }, [attemptsFigure, isError, isLoading, metrics, scopeFigure]);

  const filtersEcho: MetricsFilters = metrics?.context?.filters ?? {
    from: appliedFilters.from,
    to: appliedFilters.to,
    gated: appliedFilters.gated,
    excludePasteHeavy: appliedFilters.excludePasteHeavy,
    excludeFirstTryPasses: appliedFilters.excludeFirstTryPasses,
    moduleIds: appliedFilters.moduleIds,
  };

  return {
    definition,
    appliedFilters,
    filters: filtersEcho,
    urlParamKeys,
    setFilter,
    resetFilters,
    figure,
    context: metrics?.context,
    metrics: metrics as MetricsResponse | null,
    status,
    isFetching,
    error,
    refetch,
    scopeFigure,
    attemptsFigure,
  };
}

export type UseDashboardResult = ReturnType<typeof useDashboard>;
